import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const POLL_INTERVAL_MS = 60000;

const NotificationsContext = createContext(null);

// Base64url → Uint8Array, the form PushManager.subscribe expects for the VAPID key.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export const pushSupported = () =>
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

// One shared poll of the real notification feed, consumed by the TopBar bell, the
// notifications page and the Sidebar's payroll badge — so they don't each run
// their own timer against the same endpoints.
export function NotificationsProvider({ children }) {
  const { token, user } = useAuth();
  const canManageUsers = !!user?.permissions?.includes('manage_users');

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingWithdrawalsCount, setPendingWithdrawalsCount] = useState(0);
  const [pushState, setPushState] = useState('unsupported'); // unsupported | denied | off | on

  const authFetch = useCallback((url, opts = {}) => fetch(url, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(opts.headers || {}) },
  }), [token]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await authFetch('/api/notifications?limit=30');
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch {
      // best-effort — the feed is not on the critical path
    }
  }, [authFetch, token]);

  // Kept for the Payroll nav badge, which counts work waiting on an admin
  // rather than notifications addressed to them.
  const fetchWithdrawals = useCallback(async () => {
    if (!canManageUsers || !token) return;
    try {
      const res = await authFetch('/api/withdrawals?status=pending');
      if (!res.ok) return;
      const data = await res.json();
      setPendingWithdrawalsCount((data.requests || []).length);
    } catch {
      // best-effort
    }
  }, [authFetch, canManageUsers, token]);

  const refresh = useCallback(() => {
    fetchNotifications();
    fetchWithdrawals();
  }, [fetchNotifications, fetchWithdrawals]);

  useEffect(() => {
    if (!token) return undefined;
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refresh, token]);

  // ─── Read state ────────────────────────────────────────────────────────────
  // Updated optimistically so the badge responds instantly; a failed write just
  // re-syncs from the server.

  const markRead = useCallback(async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try { await authFetch(`/api/notifications/${id}/read`, { method: 'POST' }); } catch { refresh(); }
  }, [authFetch, refresh]);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() })));
    setUnreadCount(0);
    try { await authFetch('/api/notifications/read-all', { method: 'POST' }); } catch { refresh(); }
  }, [authFetch, refresh]);

  const remove = useCallback(async (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    try { await authFetch(`/api/notifications/${id}`, { method: 'DELETE' }); } finally { refresh(); }
  }, [authFetch, refresh]);

  // ─── Web push ──────────────────────────────────────────────────────────────

  /**
   * Read the *actual* browser state: permission, plus whether this device
   * already holds a subscription.
   *
   * getRegistration() is called with no argument on purpose. Its parameter is a
   * **client URL**, not a script URL — passing '/sw.js' happened to work only
   * because the worker's scope is '/', which covers that path by accident. No
   * argument means "the registration controlling this page", which is what is
   * actually being asked.
   */
  const syncPushState = useCallback(async () => {
    if (!pushSupported()) { setPushState('unsupported'); return; }
    if (Notification.permission === 'denied') { setPushState('denied'); return; }
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      setPushState(sub ? 'on' : 'off');
    } catch {
      setPushState('off');
    }
  }, []);

  useEffect(() => { syncPushState(); }, [syncPushState, token]);

  // Permission can change *outside the app* — in the browser's site settings,
  // or on the OS. Without this the UI kept whatever it read at mount, so
  // someone who unblocked notifications in their browser came back to a toggle
  // still insisting they were blocked, on every page, until a full reload.
  useEffect(() => {
    if (!pushSupported() || !navigator.permissions?.query) return undefined;
    let status;
    let cancelled = false;
    navigator.permissions.query({ name: 'notifications' })
      .then((s) => {
        if (cancelled) return;
        status = s;
        status.addEventListener('change', syncPushState);
      })
      // Safari has historically rejected this query for 'notifications'; the
      // visibility listener below is the fallback and covers the same case.
      .catch(() => {});
    return () => {
      cancelled = true;
      status?.removeEventListener('change', syncPushState);
    };
  }, [syncPushState]);

  // Coming back to the tab re-reads it too: changing a site permission usually
  // means leaving the tab and returning, and not every browser fires the
  // permission change event above.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') syncPushState(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [syncPushState]);

  const enablePush = useCallback(async () => {
    if (!pushSupported()) throw new Error('This browser does not support push notifications');

    // Must be called from a user gesture, which is why this is only ever
    // invoked straight off a click handler and never on mount.
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      setPushState(permission === 'denied' ? 'denied' : 'off');
      throw new Error('Notification permission was not granted');
    }

    const registration = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;

    const keyRes = await authFetch('/api/notifications/push/key');
    if (!keyRes.ok) throw new Error('Could not fetch the push key');
    const { publicKey } = await keyRes.json();

    const existing = await registration.pushManager.getSubscription();
    const subscription = existing || await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

    const res = await authFetch('/api/notifications/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription.toJSON()),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not register this device');

    setPushState('on');
  }, [authFetch]);

  const disablePush = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await authFetch('/api/notifications/push/unsubscribe', {
          method: 'POST',
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
        await sub.unsubscribe();
      }
    } finally {
      setPushState('off');
    }
  }, [authFetch]);

  const sendTestPush = useCallback(async () => {
    const res = await authFetch('/api/notifications/push/test', { method: 'POST' });
    if (!res.ok) throw new Error('Could not send the test notification');
    setTimeout(refresh, 800);
  }, [authFetch, refresh]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        pendingWithdrawalsCount,
        refresh,
        markRead,
        markAllRead,
        remove,
        pushState,
        enablePush,
        disablePush,
        sendTestPush,
        syncPushState,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  // A no-op shape rather than a throw: the bell renders inside layouts that are
  // also used by pages mounted outside the provider in tests.
  if (!ctx) {
    return {
      notifications: [], unreadCount: 0, pendingWithdrawalsCount: 0,
      refresh: () => {}, markRead: () => {}, markAllRead: () => {}, remove: () => {},
      pushState: 'unsupported', enablePush: async () => {}, disablePush: async () => {},
      sendTestPush: async () => {}, syncPushState: async () => {},
    };
  }
  return ctx;
}
